import type { RefObject } from 'react';
import { StyleSheet, Text } from 'react-native';
import Popover, { PopoverPlacement } from 'react-native-popover-view';

import { Colors } from '@/constants/theme';
import { Spacing, BorderRadius } from '@/constants/spacing';
import { useColorScheme } from '@/hooks/use-color-scheme';

type InfoPopoverProps = {
  visible: boolean;
  onClose: () => void;
  message: string;
  from: RefObject<any>;
  placement?: PopoverPlacement;
};

/**
 * InfoPopover - Lightweight popover for displaying informational messages
 *
 * Uses react-native-popover-view for positioned popovers anchored to trigger elements.
 * Used for timestamps and brief informational messages.
 */
export function InfoPopover({
  visible,
  onClose,
  message,
  from,
  placement = PopoverPlacement.AUTO,
}: InfoPopoverProps) {
  const colorScheme = useColorScheme();
  const palette = Colors[colorScheme ?? 'light'];

  return (
    <Popover
      isVisible={visible}
      onRequestClose={onClose}
      from={from}
      placement={placement}
      animationConfig={{ duration: 150, delay: 0 }}
      backgroundStyle={styles.background}
      arrowSize={{ width: 0, height: 0 }}
      popoverStyle={[
        styles.popover,
        {
          backgroundColor: palette.cardBackground,
          borderColor: palette.border,
          shadowColor: palette.text,
        },
      ]}>
      <Text style={[styles.message, { color: palette.text }]}>{message}</Text>
    </Popover>
  );
}

const styles = StyleSheet.create({
  background: {
    backgroundColor: 'transparent',
  },
  popover: {
    paddingHorizontal: Spacing.md,
    paddingVertical: Spacing.sm,
    borderRadius: BorderRadius.sm,
    borderWidth: 1,
    maxWidth: 280,
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.15,
    shadowRadius: 8,
    elevation: 10,
    zIndex: 9999,
  },
  message: {
    fontSize: 12,
    lineHeight: 16,
  },
});
